/**
 * Markdown Type Detector
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITypeDetector } from '@/interfaces/ITypeDetector';

export const MarkdownDetector: ITypeDetector = {
  id: 'markdown-detector',
  name: 'Markdown Detector',
  priority: 40, // Low priority - only if nothing else matches
  detectedType: SEMANTIC_TYPES.MARKDOWN,

  detect(input: string): ISemanticType | null {
    const trimmed = input.trim();
    
    if (trimmed.length < 3) {
      return null;
    }

    // Count markdown patterns
    let mdPatterns = 0;
    
    // Headers: # Title, ## Title, etc.
    if (/^#{1,6}\s+.+/m.test(trimmed)) {
      mdPatterns += 2;
    }

    // Bold/Italic: **text**, *text*, __text__, _text_
    if (/\*\*.+?\*\*|\*.+?\*|__.+?__|_.+?_/.test(trimmed)) {
      mdPatterns++;
    }

    // Links: [text](url)
    if (/\[.+?\]\(.+?\)/.test(trimmed)) {
      mdPatterns += 2;
    }

    // Images: ![alt](url)
    if (/!\[.*?\]\(.+?\)/.test(trimmed)) {
      mdPatterns += 2;
    }

    // Code blocks: ```code``` or `code`
    if (/```[\s\S]*?```|`[^`]+`/.test(trimmed)) {
      mdPatterns++;
    }

    // Bullet lists: - item, * item, + item
    if (/^[\s]*[-*+]\s+.+/m.test(trimmed)) {
      mdPatterns++;
    }

    // Numbered lists: 1. item
    if (/^\d+\.\s+.+/m.test(trimmed)) {
      mdPatterns++;
    }

    // Blockquotes: > text
    if (/^>\s+.+/m.test(trimmed)) {
      mdPatterns++;
    }

    // Horizontal rules: ---, ***, ___
    if (/^(---|\*\*\*|___)$/m.test(trimmed)) {
      mdPatterns++;
    }

    // Tables: | col | col |
    if (/\|.+\|/.test(trimmed) && /\|[-:]+\|/.test(trimmed)) {
      mdPatterns += 2;
    }

    // Need at least 2 patterns to be confident it's Markdown
    if (mdPatterns >= 2) {
      return SEMANTIC_TYPES.MARKDOWN;
    }

    return null;
  },
};
